import { Link, useParams, Navigate } from "react-router-dom";
import { CalendarCheck, Clock, ListChecks, MapPin, Phone, Sparkles, HeartPulse } from "lucide-react";
import { Kicker, Reveal } from "../components/Reveal";
import { ALL_SERVICES, VISIT_TYPE_LABELS, BUSINESS, formatPrice } from "../lib/data";
import { SERVICE_CONTENT } from "../lib/serviceContent";

function BulletList({ items, testid }) {
  return (
    <ul className="mt-5 space-y-3" data-testid={testid}>
      {items.map((item) => (
        <li key={item} className="flex items-start gap-3 text-sm leading-relaxed text-moss sm:text-base">
          <span className="mt-2.5 h-px w-5 shrink-0 bg-gold" />
          {item}
        </li>
      ))}
    </ul>
  );
}

export default function ServiceDetail() {
  const { serviceId } = useParams();
  const service = ALL_SERVICES.find((s) => s.id === serviceId);
  const content = SERVICE_CONTENT[serviceId];

  if (!service || !content) return <Navigate to="/" replace />;

  const related = ALL_SERVICES.filter((s) => s.category === service.category && s.id !== serviceId).slice(0, 3);

  const sections = [
    { id: "expect", title: "What to Expect", icon: Sparkles, items: content.expect },
    { id: "prep", title: "How to Prepare", icon: ListChecks, items: content.prep },
    { id: "aftercare", title: "Aftercare", icon: HeartPulse, items: content.aftercare },
  ];

  return (
    <main data-testid={`service-detail-${serviceId}`} className="bg-ivory">
      <section className="border-b border-linen bg-ivory pb-14 pt-16 sm:pb-16">
        <div className="mx-auto max-w-7xl px-6 sm:px-8">
          <Reveal>
            <Kicker>{service.category}</Kicker>
            <h1 data-testid="service-detail-title" className="mt-4 max-w-3xl font-serif text-4xl font-medium leading-tight tracking-tight text-forest sm:text-5xl lg:text-6xl">
              {service.name}
            </h1>
            <p className="mt-4 max-w-xl text-base text-moss sm:text-lg">{content.tagline}</p>
            <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-3 text-sm text-moss">
              <span className="font-serif text-3xl font-medium text-forest">{formatPrice(service.price)}</span>
              <span className="flex items-center gap-2"><Clock size={15} /> {service.duration} min</span>
              <span className="rounded-md border border-linen px-3 py-1 text-xs font-semibold uppercase tracking-[0.15em] text-forest">{VISIT_TYPE_LABELS[service.visitType]}</span>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 py-16 sm:px-8 sm:py-24">
        <div className="grid gap-12 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <Reveal>
              <p data-testid="service-detail-about" className="text-base leading-relaxed text-moss sm:text-lg">{content.about}</p>
            </Reveal>
            <div className="mt-12 space-y-12">
              {content.benefits && (
                <Reveal>
                  <div data-testid="service-detail-benefits" className="rounded-lg border border-linen p-7 sm:p-9">
                    <h2 className="font-serif text-2xl font-medium text-forest">{content.benefits.title}</h2>
                    <div className="mt-6 grid gap-6 sm:grid-cols-2">
                      {content.benefits.items.map((b) => (
                        <div key={b.title} className="flex items-start gap-4">
                          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-cream text-forest"><b.icon size={18} /></span>
                          <div>
                            <p className="text-sm font-semibold uppercase tracking-[0.1em] text-forest">{b.title}</p>
                            <p className="mt-1 text-sm text-moss">{b.text}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </Reveal>
              )}
              {sections.map((sec, i) => (
                <Reveal key={sec.id} delay={i * 0.06}>
                  <div className="rounded-lg border border-linen p-7 sm:p-9">
                    <h2 className="flex items-center gap-3 font-serif text-2xl font-medium text-forest">
                      <sec.icon size={20} className="text-moss" /> {sec.title}
                    </h2>
                    <BulletList items={sec.items} testid={`service-detail-${sec.id}`} />
                  </div>
                </Reveal>
              ))}
            </div>
          </div>

          <div className="lg:col-span-5">
            <Reveal delay={0.1} className="lg:sticky lg:top-24">
              <div data-testid="service-detail-booking-card" className="rounded-lg border border-linen p-7 sm:p-9">
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-gold-dark">Reserve Your Visit</p>
                <p className="mt-3 font-serif text-4xl font-medium text-forest">{formatPrice(service.price)}</p>
                <p className="mt-1 text-sm text-moss">
                  {service.duration}-minute {VISIT_TYPE_LABELS[service.visitType].toLowerCase()} visit — no card required to book.
                </p>
                <Link
                  to={`/book?service=${service.id}`}
                  data-testid="service-detail-book-button"
                  className="mt-6 flex items-center justify-center gap-2 rounded-md bg-forest px-6 py-3.5 text-sm font-semibold text-ivory transition-colors duration-300 hover:bg-forest-dark"
                >
                  <CalendarCheck size={16} /> Book {service.name}
                </Link>
                <div className="mt-6 space-y-2.5 border-t border-linen pt-5 text-sm text-moss">
                  <a href={BUSINESS.phoneHref} className="flex items-center gap-2.5 transition-colors hover:text-forest"><Phone size={14} className="text-gold-dark" /> {BUSINESS.phone}</a>
                  <p className="flex items-start gap-2.5"><MapPin size={14} className="mt-0.5 shrink-0 text-gold-dark" /> {BUSINESS.addressLines.join(", ")}</p>
                </div>
              </div>
            </Reveal>
          </div>
        </div>

        {related.length > 0 && (
          <Reveal className="mt-20">
            <h2 className="font-serif text-2xl font-medium text-forest">More in {service.category}</h2>
            <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {related.map((r) => (
                <Link
                  key={r.id}
                  to={`/services/${r.id}`}
                  data-testid={`related-service-${r.id}`}
                  className="rounded-lg border border-linen p-6 transition-colors duration-300 hover:border-forest/40"
                >
                  <p className="font-serif text-xl font-medium text-forest">{r.name}</p>
                  <p className="mt-1 text-sm text-moss">{formatPrice(r.price)} &middot; {r.duration} min</p>
                </Link>
              ))}
            </div>
          </Reveal>
        )}
      </section>
    </main>
  );
}
